import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { CloseIcon } from "../../../assets/icons";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";
import { fetchGeneratedDocumentFolders, searchMatters } from "../../../service/settings";

const WideModal = styled(Modal)`
  width: 720px;
  height: 620px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  padding: 0 24px 16px;
  flex: 1;
  overflow-y: auto;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  margin-top: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
`;

const Results = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-top: 6px;
  max-height: 180px;
  overflow-y: auto;
`;

const ResultButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-bottom: 1px solid #f3f4f6;
  background: ${(p) => (p.$selected ? "#eff6ff" : "#fff")};
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: #eff6ff;
  }
  &:last-child {
    border-bottom: 0;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 6px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #dc2626;
  margin-top: 8px;
`;

const Footer = styled.div`
  padding: 12px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Btn = styled.button`
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? "#0C53BB" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#374151")};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const getMatterLabel = (matter) =>
  matter?.display_name ||
  matter?.name ||
  matter?.client_name ||
  matter?.search_client_name ||
  `Matter #${matter?.id}`;

const normalizeGeneratedFolders = (payload) => {
  const raw = payload?.data?.data || payload?.data || payload;
  const list = Array.isArray(raw) ? raw : raw?.results || raw?.folders || [];
  return list
    .filter((folder) => folder?.id)
    .map((folder) => ({
      id: folder.id,
      name: folder.name || folder.title || `Folder #${folder.id}`,
      document_count: folder.document_count || 0,
    }));
};

export default function PackageGenerateModal({
  open,
  pkg,
  generating,
  onClose,
  onGenerate,
}) {
  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [matterSearching, setMatterSearching] = useState(false);
  const [generatedFolders, setGeneratedFolders] = useState([]);
  const [selectedGeneratedFolderId, setSelectedGeneratedFolderId] = useState("");
  const [error, setError] = useState("");
  const [folderError, setFolderError] = useState("");
  const [submitLocked, setSubmitLocked] = useState(false);

  const clearMatterDependentState = () => {
    setFolderError("");
  };

  useEffect(() => {
    if (!open) return;
    setMatterQuery("");
    setMatterResults([]);
    setSelectedMatter(null);
    setMatterSearching(false);
    setSelectedGeneratedFolderId("");
    setError("");
    setFolderError("");
    setSubmitLocked(false);
  }, [open, pkg?.id]);

  useEffect(() => {
    if (!generating) setSubmitLocked(false);
  }, [generating]);

  useEffect(() => {
    if (!open || selectedMatter || matterQuery.trim().length < 2) {
      setMatterResults([]);
      setMatterSearching(false);
      return;
    }
    let cancelled = false;
    setMatterSearching(true);
    setMatterResults([]);
    const timer = setTimeout(async () => {
      try {
        const res = await searchMatters(matterQuery.trim());
        if (cancelled) return;
        const data = res?.data || res;
        setMatterResults(data?.results || data || []);
      } catch (err) {
        if (!cancelled) {
          setMatterResults([]);
          setError("Matter search failed. Try again.");
        }
      } finally {
        if (!cancelled) setMatterSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [matterQuery, open, selectedMatter]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function loadGeneratedFolders() {
      try {
        setFolderError("");
        const res = await fetchGeneratedDocumentFolders();
        if (!cancelled) setGeneratedFolders(normalizeGeneratedFolders(res));
      } catch (err) {
        if (!cancelled) {
          setGeneratedFolders([]);
          setFolderError("Could not load Generated Documents folders. Create one in the Generated Documents tab first.");
        }
      }
    }
    loadGeneratedFolders();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedMatterLabel = useMemo(
    () => (selectedMatter ? getMatterLabel(selectedMatter) : ""),
    [selectedMatter]
  );

  if (!open || !pkg) return null;

  const handleSubmit = () => {
    if (generating || submitLocked) return;
    if (!selectedMatter?.id) {
      setError("Select a Matter first.");
      return;
    }
    setSubmitLocked(true);
    onGenerate({
      matterId: selectedMatter.id,
      generatedFolderId: selectedGeneratedFolderId || null,
    });
  };

  return (
    <Overlay>
      <WideModal>
        <CloseButton onClick={onClose} aria-label="Close package generator">
          <CloseIcon />
        </CloseButton>

        <Header>Generate Package</Header>

        <Body>
          <HelpText>
            Generate <strong>{pkg.name}</strong> using the field mappings,
            conditional sections, and file setup already saved on each template.
          </HelpText>

          <Label>Matter</Label>
          <Input
            placeholder="Search Matter by client, matter name, or number..."
            value={selectedMatter ? selectedMatterLabel : matterQuery}
            onChange={(e) => {
              setSelectedMatter(null);
              clearMatterDependentState();
              setMatterResults([]);
              setMatterSearching(false);
              setMatterQuery(e.target.value);
              setError("");
            }}
          />
          {matterSearching && <HelpText>Searching...</HelpText>}
          {matterResults.length > 0 && (
            <Results>
              {matterResults.map((matter) => (
                <ResultButton
                  key={matter.id}
                  type="button"
                  $selected={selectedMatter?.id === matter.id}
                  onClick={() => {
                    setSelectedMatter(matter);
                    clearMatterDependentState();
                    setMatterQuery(getMatterLabel(matter));
                    setMatterResults([]);
                    setMatterSearching(false);
                    setError("");
                  }}
                >
                  {getMatterLabel(matter)}
                </ResultButton>
              ))}
            </Results>
          )}

          <Label>Generated Documents Folder</Label>
          <Select
            value={selectedGeneratedFolderId}
            onChange={(e) => setSelectedGeneratedFolderId(e.target.value)}
            disabled={!selectedMatter}
          >
            <option value="">Matter root</option>
            {generatedFolders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </Select>
          <HelpText>
            These folders are managed in Settings → Generated Documents. DMS creates or reuses
            the matching folder inside the selected Matter when the package is generated.
          </HelpText>
          {folderError && <ErrorText>{folderError}</ErrorText>}
          {error && <ErrorText>{error}</ErrorText>}
        </Body>

        <Footer>
          <Btn onClick={onClose} disabled={generating}>
            Cancel
          </Btn>
          <Btn
            $primary
            onClick={handleSubmit}
            disabled={generating || submitLocked || !selectedMatter}
          >
            {generating ? "Generating..." : "Generate Package"}
          </Btn>
        </Footer>
      </WideModal>
    </Overlay>
  );
}
